import React, { useState } from 'react';
import { 
  Box, 
  Typography,
  IconButton, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  Grid,
  Paper
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import FlagIcon from '@mui/icons-material/Flag';
import VerifiedIcon from '@mui/icons-material/Verified';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';

const InstructionsDialog = ({ showInstructions, setShowInstructions }) => {

  const handleOpen = () => {
    setShowInstructions(true);
  };

  const handleClose = () => {
    setShowInstructions(false);
  };

  return (
    <>
      <Dialog 
        open={showInstructions}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            bgcolor: 'primary.main', 
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant="h5" component="div">
            How to Use the Content Flagging Tool
          </Typography>
          <IconButton 
            onClick={handleClose}
            sx={{ color: 'white' }}
            edge="end"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 3 }}>
            Help build a more trustworthy information environment by flagging misinformation
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <VerifiedIcon sx={{ mr: 1, color: 'primary.main' }} />
                  The Process
                </Typography>
                
                <List sx={{ mb: 2 }}>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Typography variant="h6" sx={{ color: 'primary.main' }}>1</Typography>
                    </ListItemIcon>
                    <ListItemText 
                      primary="Evaluate the Article"
                      secondary="Read the article carefully and assess whether it contains factual information or misinformation."
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Typography variant="h6" sx={{ color: 'primary.main' }}>2</Typography>
                    </ListItemIcon>
                    <ListItemText 
                      primary="Mark or Flag Content"
                      secondary="Use the upvote button to mark content as legitimate or the downvote button to flag potential misinformation."
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Typography variant="h6" sx={{ color: 'primary.main' }}>3</Typography>
                    </ListItemIcon>
                    <ListItemText 
                      primary="Provide Reasoning"
                      secondary="Share why you made your decision. This helps others understand your perspective and improves the quality of flagging."
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Typography variant="h6" sx={{ color: 'primary.main' }}>4</Typography>
                    </ListItemIcon>
                    <ListItemText 
                      primary="Review Key Points"
                      secondary="Examine each key point individually and flag specific misleading claims, even if the overall article seems accurate."
                    />
                  </ListItem>
                </List>

                <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 2 }}>
                  You can flag the entire article, specific key points, or both.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <FlagIcon sx={{ mr: 1, color: 'secondary.main' }} />
                  What to Look For
                </Typography>

                <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>When flagging, consider if the content:</Typography>
                
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CancelIcon fontSize="small" color="secondary" />
                    </ListItemIcon>
                    <ListItemText primary="Contains false or misleading information" />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <CancelIcon fontSize="small" color="secondary" />
                    </ListItemIcon>
                    <ListItemText primary="Misrepresents scientific or statistical data" />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <CancelIcon fontSize="small" color="secondary" />
                    </ListItemIcon>
                    <ListItemText primary="Makes claims without credible sources" />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <CancelIcon fontSize="small" color="secondary" />
                    </ListItemIcon>
                    <ListItemText primary="Takes quotes out of context" />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <CancelIcon fontSize="small" color="secondary" />
                    </ListItemIcon>
                    <ListItemText primary="Uses misleading headlines that don't match content" />
                  </ListItem>
                </List>

                <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>Mark as legitimate when content:</Typography>
                
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon fontSize="small" color="success" />
                    </ListItemIcon>
                    <ListItemText primary="Provides accurate information with reliable sources" />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon fontSize="small" color="success" />
                    </ListItemIcon>
                    <ListItemText primary="Presents data and statistics accurately" />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon fontSize="small" color="success" />
                    </ListItemIcon>
                    <ListItemText primary="Distinguishes clearly between facts and opinions" />
                  </ListItem>
                </List>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                  Controls Guide
                </Typography>

                <Grid container spacing={3} sx={{ mt: 1 }}>

                  
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <SkipNextIcon sx={{ mr: 1 }} />
                      <Typography variant="body1">
                        <strong>Skip</strong>: Move to next article
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                      <Typography variant="body1">
                        <strong>Check</strong>: Confirm key point as accurate
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <CancelIcon color="secondary" sx={{ mr: 1 }} />
                      <Typography variant="body1">
                        <strong>Cancel</strong>: Flag key point as misleading
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <HelpOutlineIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6" sx={{ color: 'primary.main' }}>
                    Tips for Effective Participation
                  </Typography>
                </Box>

                <List>
                  <ListItem>
                    <ListItemText 
                      primary="Be specific in your reasons when flagging content"
                      secondary="Instead of 'This is fake', specify 'The article claims X but the linked source actually states Y'"
                    />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemText 
                      primary="Evaluate each key point separately"
                      secondary="Even credible articles can contain individual points that are misleading"
                    />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemText 
                      primary="Focus on facts, not opinions"
                      secondary="Flag factual inaccuracies rather than perspectives you disagree with"
                    />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemText 
                      primary="Consider the source"
                      secondary="Check the reputation of the publication and the author's expertise on the subject"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={handleClose} 
            variant="contained"
            size="large"
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InstructionsDialog;